// `data` is intentionally `any` here: callers pass three different shapes
// (property listing rows, order items, cart items) where address fields
// can be either flat (`data.address1`) or nested (`data.property.address1`).
// Tightening to a structural type is a follow-up — banked at Pass 9 close,
// re-banked at Pass 14 for the same reason.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getPropertyAddress = (data: any): string => {
  const address1 = removeRepeated(
    data.address1 || data.property?.address1 || ''
  );
  const address2 = data.address2 || data.property?.address2 || '';
  const unitNumber = data.unitNumber || data.property?.unitNumber || '';

  // Determine if unit number should be included based on PMS source and property type
  let shouldIncludeUnit = true;
  if (data.pmsSource?.toLowerCase() === 'buildium') {
    if (
      data.propertyType === 'Single Family' ||
      data.propertyType === 'Single-Family' ||
      data.propertyType === 'SingleFamily'
    ) {
      shouldIncludeUnit = false;
    }
  }

  const city = data.city || data.property?.city || '';
  const state = data.state || data.property?.state || '';
  const zipCode = data.zipCode || data.property?.zipCode || '';

  // Build the address lines
  const addressLines: string[] = [];

  // First line: address1
  if (address1) {
    addressLines.push(address1);
  }

  // Second line: address2 and/or unit number
  let secondLine = '';
  if (address2) {
    secondLine += address2;
  }
  if (shouldIncludeUnit && unitNumber) {
    if (secondLine) {
      secondLine += `, ${unitNumber}`;
    } else {
      secondLine += unitNumber;
    }
  }
  if (secondLine) {
    addressLines.push(secondLine);
  }

  // Third line: city, state, zip
  let thirdLine = '';
  if (city) {
    thirdLine += city;
  }
  if (state) {
    if (thirdLine) {
      thirdLine += `, ${state}`;
    } else {
      thirdLine += state;
    }
  }
  if (zipCode) {
    if (thirdLine) {
      thirdLine += ` ${zipCode}`;
    } else {
      thirdLine += zipCode;
    }
  }
  if (thirdLine) {
    addressLines.push(thirdLine);
  }

  // Join lines with line breaks and clean up
  let formattedAddress = addressLines.join('\n');

  // Apply existing cleanup functions
  formattedAddress = removeAbbreviationsIfFullNameExists(
    removeDuplicateText(formattedAddress)
  );

  return cleanDoubleCommas(formattedAddress);
};

const cleanDoubleCommas = (text: string) => {
  // Replace occurrences of ',,' with ',' while ensuring not to remove valid single commas
  return text.replace(/,,+/g, ',').trim();
};

const removeRepeated = (text: string): string => {
  if (text) {
    const stringSet: Set<string> = new Set(text.split(', '));
    const nonRepeatText = [...stringSet].toString().replace(/,/g, ', ');
    return nonRepeatText;
  } else {
    return '';
  }
};

const stateSet = new Set([
  'Alabama',
  'AK',
  'Alaska',
  'AZ',
  'Arizona',
  'AR',
  'Arkansas',
  'CA',
  'California',
  'CO',
  'Colorado',
  'CT',
  'Connecticut',
  'DE',
  'Delaware',
  'DC',
  'District of Columbia',
  'FL',
  'Florida',
  'GA',
  'Georgia',
  'HI',
  'Hawaii',
  'ID',
  'Idaho',
  'IL',
  'Illinois',
  'IN',
  'Indiana',
  'IA',
  'Iowa',
  'KS',
  'Kansas',
  'KY',
  'Kentucky',
  'LA',
  'Louisiana',
  'ME',
  'Maine',
  'MD',
  'Maryland',
  'MA',
  'Massachusetts',
  'MI',
  'Michigan',
  'MN',
  'Minnesota',
  'MS',
  'Mississippi',
  'MO',
  'Missouri',
  'MT',
  'Montana',
  'NE',
  'Nebraska',
  'NV',
  'Nevada',
  'NH',
  'New Hampshire',
  'NJ',
  'New Jersey',
  'NM',
  'New Mexico',
  'NY',
  'New York',
  'NC',
  'North Carolina',
  'ND',
  'North Dakota',
  'OH',
  'Ohio',
  'OK',
  'Oklahoma',
  'OR',
  'Oregon',
  'PA',
  'Pennsylvania',
  'PR',
  'Puerto Rico',
  'RI',
  'Rhode Island',
  'SC',
  'South Carolina',
  'SD',
  'South Dakota',
  'TN',
  'Tennessee',
  'TX',
  'Texas',
  'UT',
  'Utah',
  'VT',
  'Vermont',
  'VA',
  'Virginia',
  'VI',
  'Virgin Islands',
  'WA',
  'Washington',
  'WV',
  'West Virginia',
  'WI',
  'Wisconsin',
  'WY',
  'Wyoming'
]);

const removeAbbreviationsIfFullNameExists = (text: string) => {
  let result = text;

  // Check each item in the set
  stateSet.forEach((item) => {
    const isAbbreviation = item.length === 2; // Abbreviation is usually 2 characters long
    const abbreviation = isAbbreviation ? item : '';
    const fullName = isAbbreviation ? '' : item + ', ';

    if (
      abbreviation &&
      result.includes(abbreviation) &&
      result.includes(fullName)
    ) {
      result = result
        .replace(abbreviation + ' ,', '')
        .replace(/\s{2,}/g, ' ')
        .trim();
    }
  });

  return result;
};

const removeDuplicateText = (text: string) => {
  // Regular expression to find repeated occurrences of text
  const regex = /(\b\w+\b)(?=.*\b\1\b)/g;

  return text
    .replace(regex, (match, p1, offset, string) => {
      // Replace only the first occurrence of the repeated text
      const firstOccurrenceIndex = string.indexOf(p1);
      return offset === firstOccurrenceIndex ? '' : match;
    })
    .replace(/\s{2,}/g, ' ')
    .trim(); // Clean up any extra spaces
};
